import { NavBar } from 'components/NavBar/NavBar'
import { NoItems } from 'components/NoItems-fallback/NoItems'
import { Pagination } from 'components/Pagination/Pagination'
import { FilterEnum, LinkEnum } from 'components/Filter/SingleFilter'
import { HonestTable, IHonestTableColumn } from 'components/HonestTable/HonestTable'
import { Button } from 'components/ui/Button/Button'
import { BackHeader } from 'components/ui/BackHeader/BackHeader'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import { clearFleetMachinesList, getFleetMachines } from 'features/machinery/fleetMachinesSlice'
import useQueryPagination from 'hooks/useQueryPagination'
import { useState } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { RootState } from 'store'
import { getMachineCategoryIcon } from 'pages/Machinery/machineCategoryIcons'
import { Flex, PageFooter, PageWrapper } from 'styling/GlobalStyles'
import { getParams, handlePagination } from 'utils/helpers'
import styled from 'styled-components'
import { tokens } from 'styling/tokens'
import { IStatusPill } from 'types/ui/statusPill.interface'

const operationalStatusMap: Record<number, { label: string; tone: IStatusPill['status'] }> = {
  1: { label: 'Idle', tone: 'neutral' },
  2: { label: 'Running', tone: 'info' },
}

const availabilityStatusMap: Record<number, { label: string; tone: IStatusPill['status'] }> = {
  1: { label: 'Available', tone: 'success' },
  2: { label: 'In Use', tone: 'accent' },
}

const healthStatusMap: Record<number, { label: string; tone: IStatusPill['status'] }> = {
  1: { label: 'Healthy', tone: 'success' },
  2: { label: 'Broken', tone: 'danger' },
  3: { label: 'In Maint.', tone: 'warning' },
}

const BadgesWrap = styled.div`
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
`

const FuelText = styled.span<{ value: number }>`
  color: ${({ value }) =>
    value <= 20 ? tokens.status.danger.fg : value <= 40 ? tokens.accent.base : tokens.text.primary};
  font-size: 0.85rem;
  font-weight: 600;
`

const CountText = styled.span`
  font-size: 0.85rem;
  color: ${tokens.text.muted};
  white-space: nowrap;
  margin-left: 8px;
`

export const FleetMachines = () => {
  const [params, setParams] = useState<any>(getParams({ location }))
  const navigate = useNavigate()
  const { machines, totalPages, totalCount, noMachines } = useSelector(
    (state: RootState) => state.fleetMachines,
  )

  useQueryPagination({
    fetchFunctions: [getFleetMachines],
    cleanUpFunctions: [clearFleetMachinesList],
    params,
  })

  const filters = [
    {
      type: FilterEnum.ASYNC_SELECT,
      name: 'Category',
      link: LinkEnum.MACHINE_CATEGORIES,
      propName: 'machineCategoryIds',
    },
    {
      type: FilterEnum.SELECT,
      name: 'Health',
      propName: 'healthStatuses',
      options: [
        { label: 'Healthy', value: 1 },
        { label: 'Broken', value: 2 },
        { label: 'In Maintenance', value: 3 },
      ],
    },
    {
      type: FilterEnum.SELECT,
      name: 'Availability',
      propName: 'availabilityStatuses',
      options: [
        { label: 'Available', value: 1 },
        { label: 'In Use', value: 2 },
      ],
    },
  ]

  const columns: IHonestTableColumn<any>[] = [
    { id: 'name', header: 'Name', cell: (m) => m.name },
    {
      id: 'category',
      header: 'Category',
      cell: (m) => (
        <Flex center gap='0.5rem'>
          {getMachineCategoryIcon(m.category?.iconName, tokens.accent.base, 16)}
          <span>{m.category?.name ?? '-'}</span>
        </Flex>
      ),
    },
    { id: 'model', header: 'Model', cell: (m) => m.machineModel?.name ?? '-' },
    {
      id: 'state',
      header: 'State',
      minWidth: '15rem',
      cell: (m) => {
        const opStatus = operationalStatusMap[m.operationalStatus]
        const avStatus = availabilityStatusMap[m.availabilityStatus]
        const hlStatus = healthStatusMap[m.healthStatus]
        return (
          <BadgesWrap>
            {opStatus && <StatusPill status={opStatus.tone}>{opStatus.label}</StatusPill>}
            {avStatus && <StatusPill status={avStatus.tone}>{avStatus.label}</StatusPill>}
            {hlStatus && <StatusPill status={hlStatus.tone}>{hlStatus.label}</StatusPill>}
          </BadgesWrap>
        )
      },
    },
    {
      id: 'operator',
      header: 'Operator',
      cell: (m) => (m.operator ? `${m.operator.firstName} ${m.operator.lastName}` : '-'),
    },
    { id: 'location', header: 'Location', cell: (m) => m.location?.name ?? '-' },
    {
      id: 'fuel',
      header: 'Fuel',
      align: 'right',
      cell: (m) => <FuelText value={m.fuelPercentage}>{m.fuelPercentage}%</FuelText>,
    },
  ]

  return (
    <>
      <NavBar
        input
        placeholder='Search by name or model...'
        params={params}
        setParams={setParams}
        filterOptions={filters}
        visibleFilterOverflow
        enableTemporaryManualClosing
      />
      <PageWrapper>
        <BackHeader title='Fleet' backTo='/machinery/fleet-overview'>
          <Button variant='secondary' onClick={() => navigate('/machinery/fleet/categories')}>
            Categories
          </Button>
          <Button variant='primary' onClick={() => navigate('/machinery/add')}>
            Add Machine
          </Button>
        </BackHeader>

        {noMachines ? (
          <NoItems marginTop='40px' title='Machines' />
        ) : (
          <HonestTable
            columns={columns}
            data={machines}
            keyField='id'
            onClick={(m) => navigate('/machinery/machine/' + m.id)}
          />
        )}

        {totalCount > 0 && (
          <Flex style={{ justifyContent: 'flex-end', marginTop: 8 }}>
            <CountText>
              Showing {machines.length} of {totalCount}
            </CountText>
          </Flex>
        )}
      </PageWrapper>
      <PageFooter>
        <Pagination
          setPage={(page) => handlePagination(page, setParams, 30)}
          pageCount={Math.ceil(totalPages)}
          forcePage={(params.page ? params.page : 1) - 1}
        />
      </PageFooter>
    </>
  )
}
